/**
 * AI.GEN lines=1-120
 * 统一枚举定义文件
 * 所有枚举都定义在此文件中，便于统一管理
 */
import i18n from '@/locales/i18n';

// ========== 用户权限相关 ==========

/**
 * 用户权限类型枚举
 * - Enabled: 已启用（有权限）
 * - Disabled: 已禁用（无权限）
 */
export enum UserPermissionType {
  /** 已禁用（无权限） */
  Disabled = 0,
  /** 已启用（有权限） */
  Enabled = 1,
}

// ========== 国家相关 ==========

/**
 * AI.GEN lines=1-10
 * 国家代码类型
 */
export type ICountryCode =
  | 'CN'
  | 'JP'
  | 'KR'
  | 'CA'
  | 'USA'
  | 'AUS'
  | 'NZD'
  | 'UK'
  | 'EUR'
  | 'SG';

/**
 * AI.GEN lines=1-10
 * 国家 token 名
 */
export const tokenNameEnum: Record<string, string> = {
  NZD: 'NZD_token',
  EUR: 'EUR_token',
  USA: 'USA_token',
  AUS: 'AUS_token',
  CA: 'CA_token',
  JP: 'JP_token',
  KR: 'KR_token',
  SG: 'SG_token',
  UK: 'UK_token',
};

/**
 * 域名枚举
 */
export const domainEnum: Record<string, string> = {
  release: '.hungrypanda.co',
  beta: '.hungrypanda.it',
  dev: '.hungrypanda.cn',
  test: '.hungrypanda.cn',
};

// ========== 订单业务相关 ==========

/**
 * 订单业务类型枚举
 */
export enum OrderBusinessType {
  /** HP订单 */
  HP = 0,
  /** PF订单 */
  PF = 1,
}

/**
 * AI.GEN lines=1-2
 * 订单业务类型枚举描述
 */
export const OrderBusinessTypeDesc: Record<OrderBusinessType, () => string> = {
  [OrderBusinessType.HP]: () =>
    i18n.t('dispatchWorkbench.enums.orderBusinessType.HP'),
  [OrderBusinessType.PF]: () =>
    i18n.t('dispatchWorkbench.enums.orderBusinessType.PF'),
};

/**
 * AI.GEN lines=1-40
 * 查询类型枚举（对应待指派子状态的API枚举值）
 * 包含待指派、配送中、已完成、已取消、异常等所有状态
 */
export enum QueryTypeEnum {
  // 待指派
  /** 待指派 */
  WAIT_DISPATCH = 10000,
  /** 人工列表 */
  MANAUAL = 10001,
  /** 压单 */
  AUTO_PRESS = 10002,
  /** 首次计算 */
  AUTO_FIRST = 10003,
  /** 失败重算 */
  AUTO_FAIL = 10004,
  /** 撤单重算 */
  AUTO_CANCEL = 10005,
  /** 合单 */
  AUTO_COMBINED = 10006,
  /** 强制派单待接单 */
  WAIT_FORCE = 10007,
  /** 非强制派单待接单 */
  WAIT_UNFORCE = 10008,
  /** 三方派单 */
  WAIT_THRID = 10009,
  /** 挂起 */
  PUTON = 10010,
  /** 智能派单 */
  AUTO = 10011,
  /** 待确认 */
  WAIT = 10012,
  // 配送中
  /** 配送中 */
  ING = 20000,
  /** 取餐超时 */
  ING_TAKE_MEAL = 20001,
  /** 出餐超时 */
  ING_OUT_MEAL = 20002,
  /** 超地区标准 */
  ING_AREA_OUT = 20003,
  /** 骑手考核超时 */
  ING_DRIVER_OUT = 20004,
  /** 骑手考核即将超时 */
  ING_DRIVER_WILL_OUT = 20005,
  /** 超60min */
  ING_60MIN = 20006,
  /** 3mile内40-60min */
  ING_3MILE_40_60 = 20007,
  // 已完成
  /** 已完成 */
  COMPLETE = 30000,
  // 已取消
  /** 已取消 */
  USER_CANCEL = 40000,
  // 异常
  /** 异常 */
  EXCEPTION = 50000,
  /** 撤单申请 */
  EXCEPTION_CANCELLATION = 50001,
  /** 用户失联 */
  EXCEPTION_USER_LOSS = 50002,
  /** 收餐码异常 */
  EXCEPTION_FOOD_CODE = 50003,
  /** 交付异常 */
  EXCEPTION_DELIVERY_FAIL = 50004,
  /** 催取餐 */
  EXCEPTION_URGE_MEAL = 50005,
}

/**
 * AI.GEN lines=1-2
 * 查询类型枚举描述
 */
export const QueryTypeEnumDesc: Record<QueryTypeEnum, () => string> = {
  [QueryTypeEnum.WAIT_DISPATCH]: () =>
    i18n.t('dispatchWorkbench.enums.queryType.WAIT_DISPATCH'),
  [QueryTypeEnum.MANAUAL]: () =>
    i18n.t('dispatchWorkbench.enums.queryType.MANAUAL'),
  [QueryTypeEnum.AUTO_PRESS]: () =>
    i18n.t('dispatchWorkbench.enums.queryType.AUTO_PRESS'),
  [QueryTypeEnum.AUTO_FIRST]: () =>
    i18n.t('dispatchWorkbench.enums.queryType.AUTO_FIRST'),
  [QueryTypeEnum.AUTO_FAIL]: () =>
    i18n.t('dispatchWorkbench.enums.queryType.AUTO_FAIL'),
  [QueryTypeEnum.AUTO_CANCEL]: () =>
    i18n.t('dispatchWorkbench.enums.queryType.AUTO_CANCEL'),
  [QueryTypeEnum.AUTO_COMBINED]: () =>
    i18n.t('dispatchWorkbench.enums.queryType.AUTO_COMBINED'),
  [QueryTypeEnum.WAIT_FORCE]: () =>
    i18n.t('dispatchWorkbench.enums.queryType.WAIT_FORCE'),
  [QueryTypeEnum.WAIT_UNFORCE]: () =>
    i18n.t('dispatchWorkbench.enums.queryType.WAIT_UNFORCE'),
  [QueryTypeEnum.WAIT_THRID]: () =>
    i18n.t('dispatchWorkbench.enums.queryType.WAIT_THRID'),
  [QueryTypeEnum.PUTON]: () =>
    i18n.t('dispatchWorkbench.enums.queryType.PUTON'),
  [QueryTypeEnum.AUTO]: () => i18n.t('dispatchWorkbench.enums.queryType.AUTO'),
  [QueryTypeEnum.WAIT]: () => i18n.t('dispatchWorkbench.enums.queryType.WAIT'),
  [QueryTypeEnum.ING]: () => i18n.t('dispatchWorkbench.enums.queryType.ING'),
  [QueryTypeEnum.ING_TAKE_MEAL]: () =>
    i18n.t('dispatchWorkbench.enums.queryType.ING_TAKE_MEAL'),
  [QueryTypeEnum.ING_OUT_MEAL]: () =>
    i18n.t('dispatchWorkbench.enums.queryType.ING_OUT_MEAL'),
  [QueryTypeEnum.ING_AREA_OUT]: () =>
    i18n.t('dispatchWorkbench.enums.queryType.ING_AREA_OUT'),
  [QueryTypeEnum.ING_DRIVER_OUT]: () =>
    i18n.t('dispatchWorkbench.enums.queryType.ING_DRIVER_OUT'),
  [QueryTypeEnum.ING_DRIVER_WILL_OUT]: () =>
    i18n.t('dispatchWorkbench.enums.queryType.ING_DRIVER_WILL_OUT'),
  [QueryTypeEnum.ING_60MIN]: () =>
    i18n.t('dispatchWorkbench.enums.queryType.ING_60MIN'),
  [QueryTypeEnum.ING_3MILE_40_60]: () =>
    i18n.t('dispatchWorkbench.enums.queryType.ING_3MILE_40_60'),
  [QueryTypeEnum.COMPLETE]: () =>
    i18n.t('dispatchWorkbench.enums.queryType.COMPLETE'),
  [QueryTypeEnum.USER_CANCEL]: () =>
    i18n.t('dispatchWorkbench.enums.queryType.USER_CANCEL'),
  [QueryTypeEnum.EXCEPTION]: () =>
    i18n.t('dispatchWorkbench.enums.queryType.EXCEPTION'),
  [QueryTypeEnum.EXCEPTION_CANCELLATION]: () =>
    i18n.t('dispatchWorkbench.enums.queryType.EXCEPTION_CANCELLATION'),
  [QueryTypeEnum.EXCEPTION_USER_LOSS]: () =>
    i18n.t('dispatchWorkbench.enums.queryType.EXCEPTION_USER_LOSS'),
  [QueryTypeEnum.EXCEPTION_FOOD_CODE]: () =>
    i18n.t('dispatchWorkbench.enums.queryType.EXCEPTION_FOOD_CODE'),
  [QueryTypeEnum.EXCEPTION_DELIVERY_FAIL]: () =>
    i18n.t('dispatchWorkbench.enums.queryType.EXCEPTION_DELIVERY_FAIL'),
  [QueryTypeEnum.EXCEPTION_URGE_MEAL]: () =>
    i18n.t('dispatchWorkbench.enums.queryType.EXCEPTION_URGE_MEAL'),
};

/**
 * 搜索类型枚举
 */
export enum SearchTypeEnum {
  /** 订单号 */
  OrderNumber = 0,
  /** 门店Id */
  StoreId = 1,
  /** 门店地址 */
  StoreAddress = 2,
  /** 用户地址 */
  UserAddress = 3,
  /** 订单备注 */
  OrderRemark = 4,
}

/**
 * AI.GEN lines=1-2
 * 搜索类型枚举描述
 */
export const SearchTypeEnumDesc: Record<SearchTypeEnum, () => string> = {
  [SearchTypeEnum.OrderNumber]: () =>
    i18n.t('dispatchWorkbench.enums.searchType.OrderNumber'),
  [SearchTypeEnum.StoreId]: () =>
    i18n.t('dispatchWorkbench.enums.searchType.StoreId'),
  [SearchTypeEnum.StoreAddress]: () =>
    i18n.t('dispatchWorkbench.enums.searchType.StoreAddress'),
  [SearchTypeEnum.UserAddress]: () =>
    i18n.t('dispatchWorkbench.enums.searchType.UserAddress'),
  [SearchTypeEnum.OrderRemark]: () =>
    i18n.t('dispatchWorkbench.enums.searchType.OrderRemark'),
};

/**
 * 排序字段枚举
 */
export enum SortFieldEnum {
  /** 距离 */
  Distance = 100,
  /** 订单金额 */
  OrderAmount = 101,
  /** 进入时长、 累计时长*/
  EntryDuration = 102,
  /** 已出餐时长 */
  MealPrepDuration = 103,
  /** 总累计时长 */
  TotalDuration = 104,
  /** 距考核 */
  DriverExamine = 105,
  /** 距用户超时 */
  UserExamine = 106,
  /** 待确认累计时长 */
  AccumulatedDurationPending = 107,
}

/**
 * AI.GEN lines=1-2
 * 排序字段枚举描述
 */
export const SortFieldEnumDesc: Record<SortFieldEnum, () => string> = {
  [SortFieldEnum.Distance]: () =>
    i18n.t('dispatchWorkbench.enums.sortField.Distance'),
  [SortFieldEnum.OrderAmount]: () =>
    i18n.t('dispatchWorkbench.enums.sortField.OrderAmount'),
  [SortFieldEnum.EntryDuration]: () =>
    i18n.t('dispatchWorkbench.enums.sortField.EntryDuration'),
  [SortFieldEnum.MealPrepDuration]: () =>
    i18n.t('dispatchWorkbench.enums.sortField.MealPrepDuration'),
  [SortFieldEnum.TotalDuration]: () =>
    i18n.t('dispatchWorkbench.enums.sortField.TotalDuration'),
  [SortFieldEnum.DriverExamine]: () =>
    i18n.t('dispatchWorkbench.enums.sortField.DriverExamine'),
  [SortFieldEnum.UserExamine]: () =>
    i18n.t('dispatchWorkbench.enums.sortField.UserExamine'),
  [SortFieldEnum.AccumulatedDurationPending]: () =>
    i18n.t('dispatchWorkbench.enums.sortField.AccumulatedDurationPending'),
};

/**
 * AI.GEN lines=1-5
 * 排序类型枚举
 * - Ascending: 正序
 * - Descending: 倒序
 */
export enum SortTypeEnum {
  /** 正序 */
  Ascending = 0,
  /** 倒序 */
  Descending = 1,
}

/**
 * AI.GEN lines=1-2
 * 排序类型枚举描述
 */
export const SortTypeEnumDesc: Record<SortTypeEnum, () => string> = {
  [SortTypeEnum.Ascending]: () =>
    i18n.t('dispatchWorkbench.enums.sortType.Ascending'),
  [SortTypeEnum.Descending]: () =>
    i18n.t('dispatchWorkbench.enums.sortType.Descending'),
};

/**
 * AI.GEN lines=1-10
 * 时间类型枚举
 * - OrderTime: 订（用户支付时间）
 * - DepartureTime: 出（商家出餐时间）
 * - PickupTime: 取（骑手取餐时间）
 * - CompleteTime: 完（骑手完成时间）
 */
export enum TimeTypeEnum {
  /** 订（用户支付时间） */
  OrderTime = 0,
  /** 出（商家出餐时间） */
  DepartureTime = 1,
  /** 取（骑手取餐时间） */
  PickupTime = 2,
  /** 完（骑手完成时间） */
  CompleteTime = 3,
}

/**
 * AI.GEN lines=1-5
 * 时间类型枚举描述
 */
export const TimeTypeEnumDesc: Record<TimeTypeEnum, () => string> = {
  [TimeTypeEnum.OrderTime]: () => i18n.t('dispatchWorkbench.orderItem.order'),
  [TimeTypeEnum.DepartureTime]: () =>
    i18n.t('dispatchWorkbench.orderItem.departure'),
  [TimeTypeEnum.PickupTime]: () => i18n.t('dispatchWorkbench.orderItem.pickup'),
  [TimeTypeEnum.CompleteTime]: () =>
    i18n.t('dispatchWorkbench.orderItem.complete'),
};

/**
 * Tab 类型枚举
 */
export enum OrderTabType {
  /** 待指派 */
  ToBeAssigned = 'toBeAssigned',
  /** 配送中 */
  InDelivery = 'inDelivery',
  /** 已完成 */
  Completed = 'completed',
  /** 已取消 */
  Cancelled = 'cancelled',
  /** 异常待处理 */
  AbnormalPending = 'abnormalPending',
}

/**
 * 表格展开行使用场景枚举
 */
export enum ExpandedRowVariant {
  /** 派单工作台 */
  Dispatch = 'dispatch',
  /** 盯盘工作台：隐藏关联订单模块 */
  Monitoring = 'monitoring',
}

/**
 * 待指派子状态枚举
 */
export enum ToBeAssignedSubStatus {
  /** 需人工 */
  Manual = 'manual',
  /** 智能派单中 */
  Intelligent = 'intelligent',
  /** 待确认 */
  Pending = 'pending',
  /** 挂起 */
  Suspended = 'suspended',
}

/**
 * 配送中子状态枚举
 */
export enum InDeliverySubStatus {
  /** 全部 */
  All = 'all',
  /** 撤单申请 */
  CancellationApplication = 'cancellationApplication',
  /** 取餐超时 */
  PickupTimeout = 'pickupTimeout',
  /** 出餐超时 */
  MealPrepTimeout = 'mealPrepTimeout',
  /** 超地区标准 */
  ExceedsRegionalStandard = 'exceedsRegionalStandard',
  /** 骑手考核超时 */
  RiderAssessmentTimeout = 'riderAssessmentTimeout',
  /** 骑手考核即将超时 */
  RiderAssessmentImminentTimeout = 'riderAssessmentImminentTimeout',
  /** 超60min */
  Exceeds60Min = 'exceeds60Min',
  /** 3mile内 40-60min */
  Within3Mile40To60Min = 'within3Mile40To60Min',
}

/**
 * AI.GEN lines=157-178
 * 异常处理子状态枚举
 * - CancellationApplication: 撤单申请
 * - UserOutOfContact: 用户失联
 * - MealCollectionCodeAbnormal: 收餐码异常
 * - DeliveryAbnormal: 交付异常
 * - UrgeMealCollection: 催取餐
 */
export enum AbnormalPendingSubStatus {
  /** 撤单申请 */
  CancellationApplication = 'cancellationApplication',
  /** 用户失联 */
  UserOutOfContact = 'userOutOfContact',
  /** 收餐码异常 */
  MealCollectionCodeAbnormal = 'mealCollectionCodeAbnormal',
  /** 交付异常 */
  DeliveryAbnormal = 'deliveryAbnormal',
  /** 催取餐 */
  UrgeMealCollection = 'urgeMealCollection',
}

// ========== 骑手相关 ==========

/**
 * 位置类型枚举
 */
export enum LocationTypeEnum {
  /** 送餐点 */
  Delivery = 0,
  /** 取餐点 */
  Pickup = 1,
}

/**
 * 骑手状态枚举
 */
export enum DriverStatusEnum {
  /** 离线 */
  Offline = 0,
  /** 在线 */
  Online = 1,
  /** 配送中 */
  Delivering = 2,
}

/**
 * 工作类型枚举
 */
export enum WorkTypeEnum {
  /** 专送 */
  Dedicated = 0,
  /** 众包 */
  Crowdsourcing = 1,
}

/**
 * 是否可代买枚举
 */
export enum CanInsteadOrderEnum {
  /** 否 */
  No = 0,
  /** 是 */
  Yes = 1,
}

/**
 * 骑手业务类型枚举
 */
export enum DriverBusinessTypeEnum {
  /** 仅接HP */
  OnlyHP = 0,
  /** 仅接PF */
  OnlyPF = 1,
}

/**
 * 是否可拒单枚举
 */
export enum RejectAbleEnum {
  /** 分配（强制分配，不可拒单） */
  ForceAssign = 0,
  /** 非强制分配（可拒单） */
  NonForceAssign = 1,
}

// ========== 列表配送渠道（thirdType / thirdDistribution）==========

/**
 * 配送渠道（老 TaskCenter 催取餐列表 thirdType：0=熊猫，1=三方；与 thirdDistribution 0/1 语义一致）
 */
export enum DispatchThirdChannelEnum {
  /** 熊猫配送 */
  Panda = 0,
  /** 第三方配送 */
  ThirdParty = 1,
}

/**
 * 配送渠道枚举描述
 */
export const DispatchThirdChannelEnumDesc: Record<
  DispatchThirdChannelEnum,
  string
> = {
  [DispatchThirdChannelEnum.Panda]: '熊猫配送',
  [DispatchThirdChannelEnum.ThirdParty]: '第三方配送',
};

// ========== 第三方配送相关 ==========

/**
 * AI.GEN lines=212-243
 * 第三方配送枚举
 * - LaLaMove: LaLaMove 配送
 * - Barogo: Barogo 配送
 * - Uber: Uber 配送
 * - Stuart: Stuart 配送
 * - Rida: Rida 配送
 * - Nash: Nash 配送
 * - DoorDash: DoorDash 配送
 * - Meshkorea: Meshkorea 配送（韩国特有）
 */
export enum ThirdDeliveryEnum {
  /** LaLaMove 配送 */
  Lalamove = 2,
  /** Barogo 配送 */
  Barogo = 3,
  /** Uber 配送 */
  Uber = 4,
  /** Stuart 配送 */
  Stuart = 1,
  /** Rida 配送 */
  Rida = 5,
  /** Nash 配送 */
  Nash = 6,
  /** Doordash 配送 */
  Doordash = 8,
  /** Meshkorea 配送（韩国特有） */
  Meshkorea = 7,
}

/**
 * AI.GEN lines=1-10
 * 第三方配送枚举描述
 */
export const ThirdDeliveryEnumDesc: Record<ThirdDeliveryEnum, string> = {
  [ThirdDeliveryEnum.Lalamove]: 'Lalamove',
  [ThirdDeliveryEnum.Barogo]: 'Barogo',
  [ThirdDeliveryEnum.Uber]: 'Uber',
  [ThirdDeliveryEnum.Stuart]: 'Stuart',
  [ThirdDeliveryEnum.Rida]: 'Rida',
  [ThirdDeliveryEnum.Nash]: 'Nash',
  [ThirdDeliveryEnum.Doordash]: 'Doordash',
  [ThirdDeliveryEnum.Meshkorea]: 'Meshkorea',
};

// ========== 时间颜色类型相关 ==========

/**
 * AI.GEN lines=1-5
 * 时间文案颜色类型枚举
 * - Normal: 正常颜色
 * - Red: 红色
 */
export enum ColorTypeEnum {
  /** 正常颜色 */
  Normal = 0,
  /** 红色 */
  Red = 1,
}

// ========== 列表类型相关 ==========

/**
 * AI.GEN lines=245-264
 * 列表类型枚举
 * - Auto: 自动列表
 * - Manual: 手动列表
 * - Wait: 待分配列表
 * - Suspend: 挂起列表
 */
export enum ListTypeEnum {
  /** 自动列表 */
  Auto = 'auto',
  /** 手动列表 */
  Manual = 'manual',
  /** 待分配列表 */
  Wait = 'wait',
  /** 挂起列表 */
  Suspend = 'hangUpList',
}

// ========== 订单类型相关 ==========

/**
 * AI.GEN lines=1-10
 * 订单类型枚举
 * - PandaDelivery: 显示Panda配送订单(除远距离,团送订单,专属司机配送店铺订单)
 * - PandaFaraway: 显示Panda配送远距离订单
 * - PandaSpecialDriver: 显示Panda配送「专属司机配送」商家近距离模式订单
 * - GroupOrder: 显示团送订单
 */
export enum OrderTypeEnum {
  /** 显示Panda配送订单(除远距离,团送订单,专属司机配送店铺订单) */
  PandaDelivery = 0,
  /** 显示Panda配送远距离订单 */
  PandaFaraway = 1,
  /** 显示Panda配送「专属司机配送」商家近距离模式订单 */
  PandaSpecialDriver = 2,
  /** 显示团送订单 */
  GroupOrder = 3,
}

/**
 * AI.GEN lines=1-2
 * 订单类型枚举描述
 */
export const OrderTypeEnumDesc: Record<OrderTypeEnum, () => string> = {
  [OrderTypeEnum.PandaDelivery]: () =>
    i18n.t('dispatchWorkbench.enums.orderType.PandaDelivery'),
  [OrderTypeEnum.PandaFaraway]: () =>
    i18n.t('dispatchWorkbench.enums.orderType.PandaFaraway'),
  [OrderTypeEnum.PandaSpecialDriver]: () =>
    i18n.t('dispatchWorkbench.enums.orderType.PandaSpecialDriver'),
  [OrderTypeEnum.GroupOrder]: () =>
    i18n.t('dispatchWorkbench.enums.orderType.GroupOrder'),
};

/**
 * 挂起类型枚举
 * - SetTime: 设置挂起时间
 * - SelfControl: 我要自己控制
 * - Delay: 延长时间
 * - Close: 我要提前关闭
 */
export enum HangUpTypeEnum {
  /** 设置挂起时间 */
  SetTime = 0,
  /** 我要自己控制 */
  SelfControl = 1,
  /** 延长时间 */
  Delay = 2,
  /** 我要提前关闭 */
  Close = 3,
}

/**
 * AI.GEN lines=1-2
 * 挂起类型枚举描述
 */
export const HangUpTypeEnumDesc: Record<HangUpTypeEnum, () => string> = {
  [HangUpTypeEnum.SetTime]: () => i18n.t('pages.hangUp.options.setTime'),
  [HangUpTypeEnum.SelfControl]: () =>
    i18n.t('pages.hangUp.options.selfControl'),
  [HangUpTypeEnum.Delay]: () => i18n.t('pages.hangUp.options.delay'),
  [HangUpTypeEnum.Close]: () => i18n.t('pages.hangUp.options.close'),
};

/**
 * 挂起时间类型枚举
 * - Minute: 分钟
 * - Hour: 小时
 * - Day: 天
 */
export enum HangUpTimeTypeEnum {
  /** 分钟 */
  Minute = 0,
  /** 小时 */
  Hour = 1,
  /** 天 */
  Day = 2,
}

/**
 * AI.GEN lines=1-2
 * 挂起时间类型枚举描述
 */
export const HangUpTimeTypeEnumDesc: Record<HangUpTimeTypeEnum, () => string> =
  {
    [HangUpTimeTypeEnum.Minute]: () => i18n.t('pages.hangUp.timeUnit.minute'),
    [HangUpTimeTypeEnum.Hour]: () => i18n.t('pages.hangUp.timeUnit.hour'),
    [HangUpTimeTypeEnum.Day]: () => i18n.t('pages.hangUp.timeUnit.day'),
  };

// ========== 调度工作台常量 ==========

/**
 * AI.GEN lines=1-5
 * 距离范围选项常量
 */
export const DISTANCE_RANGE_OPTIONS: readonly string[] = [
  '0-1',
  '1-2',
  '2-3',
  '3-4',
  '4-5',
  '5-6',
  '6-7',
  '7-8',
  '8-9',
  '9-10',
  '10以上',
] as const;

/**
 * AI.GEN lines=1-2
 * 距离范围"10以上"常量值
 */
export const DISTANCE_RANGE_ABOVE_10 = '10以上';

/**
 * AI.GEN lines=1-5
 * 精确搜索类型常量
 */
export const PRECISE_SEARCH_TYPE = {
  /** 订单号 */
  ORDER_NUMBER: 'orderNumber',
  /** 门店Id */
  STORE_ID: 'storeId',
  /** 门店地址 */
  STORE_ADDRESS: 'storeAddress',
  /** 用户地址 */
  USER_ADDRESS: 'userAddress',
  /** 订单备注 */
  ORDER_REMARK: 'orderRemark',
} as const;

/**
 * AI.GEN lines=1-2
 * 订单金额高亮阈值（超过此金额显示红色）
 */
export const ORDER_AMOUNT_HIGHLIGHT_THRESHOLD = 100;

/**
 * AI.GEN lines=1-2
 * 默认载具类型（汽车）
 */
export const DEFAULT_VEHICLE_TYPE = 1;

/**
 * AI.GEN lines=1-10
 * 载具类型枚举（数字类型，对应接口值）
 */
export enum VehicleTypeEnum {
  /** 摩托车 */
  Motorcycle = 0,
  /** 汽车 */
  Car = 1,
  /** 自行车 */
  Bicycle = 2,
  /** 步行 */
  Walk = 3,
  /** 电动车 */
  ElectricVehicle = 4,
}

/**
 * AI.GEN lines=1-10
 * 接单状态枚举（用于骑手列表筛选）
 */
export enum OrderReceiveStatusEnum {
  /** Auto自动 */
  Auto = 0,
  /** Manual手动 */
  Manual = 1,
}

/**
 * AI.GEN lines=1-10
 * 单量状态枚举（用于骑手列表筛选）
 */
export enum OrderQuantityStatusEnum {
  /** 有单 */
  HasOrder = 0,
  /** 空车 */
  Empty = 1,
  /** 伪上线 */
  PseudoOnline = 2,
}

/**
 * AI.GEN lines=1-10
 * 骑手搜索类型枚举
 */
export enum RiderSearchTypeEnum {
  /** 骑手姓名 */
  RiderName = 0,
  /** 骑手ID */
  RiderId = 1,
  /** 手机号 */
  PhoneNumber = 2,
  /** 备注 */
  Remark = 3,
  /** 用户昵称（searchType，与列表字段 userNick 对应） */
  UserNick = 4,
}

/**
 * AI.GEN lines=1-7
 * 骑手搜索类型枚举描述
 */
export const RiderSearchTypeEnumDesc: Record<RiderSearchTypeEnum, string> = {
  [RiderSearchTypeEnum.RiderName]: '骑手姓名',
  [RiderSearchTypeEnum.RiderId]: '骑手ID',
  [RiderSearchTypeEnum.PhoneNumber]: '手机号',
  [RiderSearchTypeEnum.Remark]: '备注',
  [RiderSearchTypeEnum.UserNick]: '骑手昵称',
};

/**
 * AI.GEN lines=1-10
 * 骑手排序类型枚举
 */
export enum RiderSortTypeEnum {
  /** 背单量 */
  OrderCount = 1,
  /** 已完成单量 */
  CompletedCount = 2,
  /** 空闲时间 */
  FreeTime = 3,
  /** 最大背单量 */
  MaxOrderCount = 4,
  /** PF到仓时间 */
  PFArrivalTime = 5,
  /** 取餐距离 */
  PickupDistance = 6,
}

/**
 * AI.GEN lines=1-10
 * 骑手排序方向枚举
 */
export enum RiderSortOrderEnum {
  /** 正序 */
  Ascending = 0,
  /** 倒序 */
  Descending = 1,
}

/**
 * AI.GEN lines=1-2
 * 距离范围最小值（用于"10以上"的判断）
 */
export const DISTANCE_RANGE_MIN_ABOVE_10 = 10;

/**
 * AI.GEN lines=1-5
 * 骑手列表分页大小
 */
export const RIDER_LIST_PAGE_SIZE = 50;

/**
 * AI.GEN lines=1-5
 * 骑手列表滚动加载触发距离（px）
 */
export const RIDER_LIST_SCROLL_LOAD_DISTANCE = 50;

/**
 * AI.GEN lines=1-20
 * 订单排序类型枚举
 * 注意：排序功能已交由服务端处理，此枚举仅用于前端表单选项
 */
export enum OrderSortType {
  /** 用户下单时间顺序排列 */
  OrderTimeAsc = '0',
  /** 用户下单时间倒序排列 */
  OrderTimeDesc = '1',
  /** 考核时间顺序排列 */
  AssessmentTimeAsc = '2',
  /** 超时敏感类订单优先 */
  OvertimeSensitiveFirst = '3',
  /** 派单倾斜订单优先 */
  DispatchTiltFirst = '4',
  /** 根据订单金额从高到低排序 */
  OrderAmountDesc = '5',
  /** 已出餐时间顺序排序 */
  MealPrepTimeAsc = '6',
  /** 根据派单时间倒序 */
  DispatchTimeDesc = '7',
}

/**
 * AI.GEN lines=1-17
 * 货币符号映射（根据国家代码）
 * - NZD: 新西兰元
 * - EUR: 欧元
 * - USA: 美元
 * - AUS: 澳元
 * - CA: 加元
 * - JP: 日元
 * - FR: 法国欧元
 * - KR: 韩元
 * - SG: 新加坡元
 * - UK: 英镑
 */
export const LEGAL_TENDER_MAP: Record<string, string> = {
  NZD: 'NZ$',
  EUR: '€',
  USA: '$',
  AUS: 'A$',
  CA: 'C$',
  JP: '¥',
  FR: '€',
  KR: '₩',
  SG: '$',
  UK: '£',
};
